// client/src/components/ChatInputContainer.js
import {
    Box,
    HStack,
    Button,
    Textarea,
    Input,
    Spinner,
    Tooltip, useToast,
} from "@chakra-ui/react";
import { Paperclip } from "lucide-react";
import { FiSend } from "react-icons/fi";
import { FiX } from "react-icons/fi";
import { useState, useRef, useEffect } from "react";


export default function ChatInput({ onSend, mode, disabled, isLoading, isReportLoading }) {
    const [value, setValue] = useState("");
    const [files, setFiles] = useState([]);
    const textRef = useRef(null);
    const tagsRef = useRef(null);
    const containerRef = useRef(null);
    const fileInputRef = useRef(null);
    const toast = useToast();
    const [isDragging, setIsDragging] = useState(false);
    const dragDepthRef = useRef(0);

    const isDisabled = isLoading || isReportLoading || disabled || (!value.trim() && files.length === 0);


    useEffect(() => {
        if (!textRef.current || !containerRef.current) return;


        const textarea = textRef.current;
        const container = containerRef.current;
        const tagsBox = tagsRef.current;


        // Reset height first
        textarea.style.height = "auto";


        const lineHeight = 22;
        let minHeight, maxHeight;


        if (mode === 'chat') {
            minHeight = lineHeight * 1; // 1 line min
            maxHeight = lineHeight * 4; // 4 lines max
        } else {
            // Hero mode
            minHeight = lineHeight * 4; // 4 lines min
            maxHeight = lineHeight * 7; // 7 lines max
        }


        // Calculate actual content height
        let contentHeight = Math.max(textarea.scrollHeight, minHeight);
        contentHeight = Math.min(contentHeight, maxHeight);


        // Set textarea height
        textarea.style.height = contentHeight + "px";


        // Measure tags height dynamically (accounts for wrapping rows)
        const tagsHeight = files.length > 0 && tagsBox ? tagsBox.offsetHeight : 0;


        // Reduced button bar height - use smaller base and less aggressive scaling
        const buttonBarHeight = mode === 'hero'
            ? Math.min(70, 60 + window.innerWidth * 0.015) // 🔧 Reduced from 6% to 1.5% and max capped at 70px
            : 60;

        // Total container height = content + tags + button bar
        container.style.height = contentHeight + tagsHeight + buttonBarHeight + "px";
    }, [value, files, mode]);

    const showToast = (title, description, status = 'warning') => {
        toast({
            title,
            description,
            status: 'info', duration: 3500, isClosable: true, position: 'top',
        });
    };
    const MAX_FILES = 4;
    const allowedTypes = [
        'application/pdf',
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/webp',
        'image/svg+xml',
        'text/plain'
    ];

    const handleFileSelect = (event) => {
        const selectedFiles = Array.from(event.target.files);

        const validFiles = selectedFiles.filter(file => {
            const isValid = allowedTypes.includes(file.type);
            if (!isValid) {
                showToast(
                    'Unsupported file type',
                    `"${file.name}" is not allowed. Use PDF, images, or text files.`
                );
            }
            return isValid;
        });

        // ✅ Check BEFORE setFiles, outside the updater
        const wouldExceed = files.length + validFiles.length > MAX_FILES;
        if (wouldExceed) {
            showToast(
                'File limit reached',
                `You can attach up to ${MAX_FILES} files per message.`
            );
        }

        // ✅ Updater is now side-effect free — no toast inside
        setFiles((prev) => {
            const combined = [...prev, ...validFiles];
            return combined.length > MAX_FILES ? combined.slice(0, MAX_FILES) : combined;
        });
        // reset input so same file can be re-selected
        event.target.value = '';
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        dragDepthRef.current += 1;
        setIsDragging(true);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
        if (dragDepthRef.current === 0) {
            setIsDragging(false);
        }
    };

    const clearDragState = () => {
        dragDepthRef.current = 0;
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        clearDragState();

        const droppedFiles = Array.from(e.dataTransfer?.files || []);
        if (droppedFiles.length > 0) {
            handleFileSelect({
                target: {
                    files: droppedFiles,
                    value: "",
                },
            });
        }
    };

    const handlePaste = (event) => {
        const pastedFiles = Array.from(event.clipboardData?.files || []);
        if (pastedFiles.length === 0) return;

        event.preventDefault();
        handleFileSelect({
            target: {
                files: pastedFiles,
                value: "",
            },
        });
    };

    useEffect(() => {
        const onWindowPaste = (e) => {
            const pastedFiles = Array.from(e.clipboardData?.files || []);
            if (pastedFiles.length > 0) {
                e.preventDefault();
                handleFileSelect({
                    target: { files: pastedFiles, value: "" },
                });
            }
        };

        const onWindowDragEnter = (e) => {
            if (e.dataTransfer?.types?.includes("Files")) {
                dragDepthRef.current += 1;
                setIsDragging(true);
            }
        };

        const onWindowDragOver = (e) => {
            if (e.dataTransfer?.types?.includes("Files")) {
                e.preventDefault();
                setIsDragging(true);
            }
        };

        const onWindowDragLeave = (e) => {
            dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
            if (dragDepthRef.current === 0) {
                setIsDragging(false);
            }
        };

        const onWindowDrop = () => {
            dragDepthRef.current = 0;
            setIsDragging(false);
        };

        const onWindowDragEnd = () => {
            dragDepthRef.current = 0;
            setIsDragging(false);
        };

        window.addEventListener("paste", onWindowPaste);
        window.addEventListener("dragenter", onWindowDragEnter);
        window.addEventListener("dragover", onWindowDragOver);
        window.addEventListener("dragleave", onWindowDragLeave);
        window.addEventListener("drop", onWindowDrop);
        window.addEventListener("dragend", onWindowDragEnd);

        return () => {
            window.removeEventListener("paste", onWindowPaste);
            window.removeEventListener("dragenter", onWindowDragEnter);
            window.removeEventListener("dragover", onWindowDragOver);
            window.removeEventListener("dragleave", onWindowDragLeave);
            window.removeEventListener("drop", onWindowDrop);
            window.removeEventListener("dragend", onWindowDragEnd);
        };
    }, []);

    const removeFile = (index) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
    };


    const handleSendClick = () => {
        if (!value.trim() && files.length === 0) return;
        if (disabled || isLoading) return;
        try {
            onSend(value, files);
            setValue('');
            setFiles([]);
        } catch (error) {
            console.error('Error sending message:', error);
        }
    };


    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendClick();
        }
    };


    const isHeroMode = mode === 'hero';


    return (
        <Box
            ref={containerRef}
            position="relative"
            width="100%"
            bg="#FBFBFB"
            border="solid black"
            borderRadius="20px"
            borderWidth={isHeroMode ? "clamp(1px, 0.15vw, 2px)" : "2px"}
            p={0}
            minH={isHeroMode ? "clamp(180px, 15vh, 300px)" : "120px"}
            sx={{
                fontSize: isHeroMode ? "clamp(14px, 1vw, 20px)" : "14px"
            }}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            {isDragging && (
                <Box
                    position="absolute"
                    inset="0"
                    zIndex={20}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    bg="rgba(255,255,255,0.88)"
                    borderRadius="20px"
                    pointerEvents="none"
                    fontWeight="semibold"
                    color="#242424"
                >
                    Drop files here
                </Box>
            )}
            {files.length > 0 && (
                <Box
                    ref={tagsRef}
                    display="flex"
                    flexWrap="wrap"
                    gap={isHeroMode ? "clamp(6px, 0.4vw, 10px)" : "6px"}
                    p={isHeroMode ? "clamp(12px, 0.8vw, 18px)" : "12px"}
                    pb={isHeroMode ? "clamp(8px, 0.6vw, 14px)" : "8px"}
                    maxH="150px"
                    overflowY="auto"
                    css={{
                        scrollbarWidth: "thin",
                        scrollbarColor: "#DADADA transparent",
                        "&::-webkit-scrollbar": {
                            width: "4px"
                        },
                        "&::-webkit-scrollbar-track": {
                            background: "transparent"
                        },
                        "&::-webkit-scrollbar-thumb": {
                            background: "#DADADA",
                            borderRadius: "10px"
                        }
                    }}
                >
                    {files.map((file, index) => (
                        <Box
                            key={index}
                            bg="#ffffff"
                            borderRadius="100px"
                            border="1px solid #DADADA"
                            px={isHeroMode ? "clamp(12px, 0.7vw, 16px)" : "12px"}
                            py={isHeroMode ? "clamp(6px, 0.4vw, 10px)" : "6px"}
                            fontSize={isHeroMode ? "clamp(12px, 0.75vw, 15px)" : "13px"}
                            fontWeight="semibold"
                            display="flex"
                            alignItems="center"
                            gap={isHeroMode ? "clamp(6px, 0.5vw, 10px)" : "6px"}
                            color="#242424"
                            flexShrink={0}
                            maxW="100%"
                            overflow="hidden"
                        >
                            <Box
                                as="span"
                                overflow="hidden"
                                textOverflow="ellipsis"
                                whiteSpace="nowrap"
                                maxW="300px"
                            >
                                {file.name}
                            </Box>
                            <Box
                                as="button"
                                fontSize={isHeroMode ? "clamp(12px, 0.7vw, 14px)" : "12px"}
                                fontWeight="bold"
                                color="#242424"
                                onClick={() => removeFile(index)}
                                flexShrink={0}
                                display="flex"
                                alignItems="center"
                            >
                                <FiX />
                            </Box>
                        </Box>
                    ))}
                </Box>
            )}


            <Input
                type="file"
                multiple
                display="none"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept="application/pdf,image/*,text/plain,.txt"
            />


            <Textarea
                ref={textRef}
                value={value}
                width="100%"
                onChange={(e) => setValue(e.target.value)}
                minHeight={isHeroMode ? "clamp(88px, 10vh, 154px)" : "22px"}
                maxHeight={isHeroMode ? "clamp(154px, 10vh, 200px)" : "88px"}
                onKeyDown={handleKeyDown}
                placeholder="Type here"
                color="#242424"
                _placeholder={{
                    color: "#6D6D6D",
                    opacity: 1,
                }}
                resize="none"
                overflowY="auto"
                border="none"
                borderRadius={isHeroMode ? "clamp(20px, 1.5vw, 32px)" : "20px"}
                fontSize={isHeroMode ? "clamp(14px, 1vw, 20px)" : "14px"}
                pt={files.length > 0 ? "0px" : (isHeroMode ? "clamp(14px, 1.5vw, 26px)" : "14px")}
                pb="0px"
                px={isHeroMode ? "clamp(12px, 1.2vw, 24px)" : "12px"}
                lineHeight="1.4"
                focusBorderColor="transparent"
                _focus={{
                    boxShadow: "none",
                    borderColor: "transparent",
                    outline: "none",
                }}
                css={{
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                    "&::-webkit-scrollbar": {
                        display: "none"
                    }
                }}
            />


            {/* Button bar - always visible with reserved space */}
            <HStack
                position="absolute"
                bottom={isHeroMode ? "clamp(12px, 0.8vw, 18px)" : "10px"}
                left={isHeroMode ? "clamp(12px, 1.2vw, 24px)" : "12px"}
                right={isHeroMode ? "clamp(12px, 1.2vw, 24px)" : "12px"}
                justify="space-between"
                boxSizing="content-box"
                zIndex={10}
                pointerEvents="auto"
            >
                <Tooltip
                    label="Supported formats: JPG, PNG, PDF, SVG, DOCX, TXT, and similar document/image files."
                    hasArrow
                    placement="top"
                    borderRadius="8px"
                    bg="#CF008A"
                    color="white"
                    fontSize="12px"
                >
                    <Button
                        size="sm"
                        leftIcon={<Paperclip size={isHeroMode ? 20 : 16} color="#111" />}
                        bg="#ffffff"
                        borderRadius="12px"
                        border="1px solid #ECECEC"
                        _hover={{ bg: "#f5f5f5" }}
                        onClick={() => fileInputRef.current.click()}
                        fontSize={isHeroMode ? "clamp(14px, 1vw, 20px)" : "14px"}
                        h={isHeroMode ? "clamp(36px, 2.8vw, 48px)" : "36px"}
                        px={isHeroMode ? "clamp(12px, 1.5vw, 26px)" : "12px"}
                        fontWeight="semibold"
                        isDisabled={files.length >= MAX_FILES}
                        opacity={files.length >= MAX_FILES ? 0.4 : 1}
                    >
                        Attach
                    </Button>
                </Tooltip>


                <Button
                    size="sm"
                    w={isHeroMode ? "clamp(36px, 2.8vw, 48px)" : "36px"}
                    h={isHeroMode ? "clamp(36px, 2.8vw, 48px)" : "36px"}
                    minW={isHeroMode ? "clamp(36px, 2.8vw, 48px)" : "36px"}
                    p={0}
                    bg={isDisabled ? "#ffffff" : "#CF008A"}
                    borderRadius="12px"
                    border="1px solid #ECECEC"
                    _hover={{ bg: isDisabled ? "#ffffff" : "#D933A1" }}
                    cursor={isDisabled ? "default" : "pointer"}
                    onClick={handleSendClick}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                >


                    {isLoading || isReportLoading ? (
                        <Spinner size="sm" color="blue.500" />
                    ) : (
                        <FiSend
                            size={isHeroMode ? 20 : 16}
                            color={isDisabled ? "#111" : "#fff"}
                        />
                    )}
                </Button>
            </HStack>
        </Box>
    );
}
